import { createContext, useContext, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Transmit } from "@adonisjs/transmit-client";
import { SSE_SILENCE_TIMEOUT_MS, shouldReviveForSilence } from "~/lib/sse-liveness";

type TransmitEvent = object;

type SubscriptionEntry = {
  count: number;
  subscription: ReturnType<Transmit["subscription"]>;
  callbacks: Set<(event: TransmitEvent) => void>;
  pendingDelete: ReturnType<typeof setTimeout> | null;
  createAttempt: number;
  create: () => Promise<void>;
};

type TransmitSubscribeOptions = {
  headers?: Record<string, string>;
};

type TransmitContextValue = {
  subscribe: (
    channel: string,
    callback: (event: TransmitEvent) => void,
    options?: TransmitSubscribeOptions,
  ) => () => void;
  onReconnect: (callback: () => void) => () => void;
};

type TransmitDiagnosticContext = Record<string, string | number | boolean | null>;

type TransmitProviderProps = {
  children: ReactNode;
  baseUrl?: string;
  onMessage?: (channel: string, event: TransmitEvent) => void;
  enableLogging?: boolean;
  // SSE lifecycle tap (connect/disconnect/subscribe attempts and failures).
  // The widget entry ships these to the server; the dashboard leaves it unset.
  onDiagnostic?: (event: string, context?: TransmitDiagnosticContext) => void;
};

const TransmitContext = createContext<TransmitContextValue | null>(null);

const SUBSCRIBE_RETRY_DELAYS_MS = [250, 500, 1_000, 2_000, 4_000];

// Cota para la recreación del cliente ante 400s: durante el overlap de un
// zero-downtime deploy cada intento puede volver a fallar; un intento cada
// 10s converge en cuanto la instancia vieja muere sin abrir una tormenta
// de EventSources.
const CLIENT_RECREATE_COOLDOWN_MS = 10_000;

// El 400 del subscribe es ambiguo: uid desconocido (recrear ayuda) o canal no
// autorizado (recrear jamás ayuda). El presupuesto corta el churn del segundo
// caso; cada suscripción exitosa lo repone porque prueba que el uid funciona.
const CLIENT_RECREATE_MAX_ATTEMPTS = 3;

// El retry nativo de EventSource muere PARA SIEMPRE si un intento de
// reconexión recibe una respuesta HTTP no-200 (spec WHATWG: "fail the
// connection" → readyState=CLOSED, sin más reintentos). En prod siempre hay un
// proxy delante, así que un deploy/restart no se ve como connection-refused
// (que el retry nativo sí sobrevive) sino como 502/503 → el SSE de cada
// pestaña abierta quedaba muerto hasta un F5. transmit-client nunca recrea su
// EventSource (solo cuenta intentos), de modo que la única recuperación es
// recrear el cliente entero desde fuera: si tras este plazo de estar en
// 'disconnected'/'reconnecting' no llegó un 'connected', se reconstruye. Un
// retry nativo sano reconecta en ~3s; 15s de sordera = EventSource cadáver.
const SSE_REVIVE_AFTER_MS = 15_000;

// Cada cuánto se comprueba el silencio del stream. No hace falta afinar: el
// plazo real lo pone SSE_SILENCE_TIMEOUT_MS.
const SSE_SILENCE_CHECK_INTERVAL_MS = 15_000;

/**
 * Provider component that manages Transmit instance and subscriptions
 * Handles connection lifecycle and subscription cleanup
 * Authentication is handled automatically via Inertia session cookies
 *
 * Uses debounced unsubscription to handle Inertia page navigations where
 * components unmount and immediately remount (e.g. non-persistent layouts).
 */
export function TransmitProvider({
  children,
  baseUrl,
  onMessage,
  enableLogging = false,
  onDiagnostic,
}: TransmitProviderProps) {
  // Create the Transmit instance lazily. The client opens EventSource in its constructor,
  // so creating it only on first subscription avoids exhausting browser connections
  // when several app tabs are open.
  const transmit = useRef<Transmit | null>(null);
  // Track active subscriptions with reference counting
  const subscriptions = useRef<Map<string, SubscriptionEntry>>(new Map());

  // Use refs for mutable values so subscribe remains stable
  const enableLoggingRef = useRef(enableLogging);
  enableLoggingRef.current = enableLogging;

  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const onDiagnosticRef = useRef(onDiagnostic);
  onDiagnosticRef.current = onDiagnostic;

  const diagnostic = useCallback((event: string, context?: TransmitDiagnosticContext) => {
    try {
      onDiagnosticRef.current?.(event, context);
    } catch {
      // @expected-error-boundary: a broken diagnostics consumer is ignored —
      // observability must never break realtime itself.
    }
  }, []);

  const reconnectCallbacks = useRef<Set<() => void>>(new Set());
  const wasDisconnected = useRef(false);

  const lastClientRecreateAt = useRef(0);
  const clientRecreatesLeft = useRef(CLIENT_RECREATE_MAX_ATTEMPTS);
  const recreateClientRef = useRef<() => void>(() => undefined);
  const pendingRecreate = useRef<ReturnType<typeof setTimeout> | null>(null);
  const providerClosed = useRef(false);

  // Estado observado del SSE (null = aún sin primer 'connected'/'disconnected')
  // y watchdog de resurrección para el EventSource cadáver (ver
  // SSE_REVIVE_AFTER_MS). `reviveClientRef` sigue el mismo patrón que
  // `recreateClientRef`: el timer llama siempre al closure commiteado.
  const sseConnected = useRef<boolean | null>(null);
  // Último byte recibido por el stream (lo alimenta el eventSourceFactory).
  const lastStreamActivityAt = useRef<number | null>(null);
  const reviveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reviveClientRef = useRef<() => void>(() => undefined);

  // Invalida las escaleras de reintento en vuelo cuando el cliente se recrea
  // o una reconexión relanza create(): un setTimeout pendiente de la escalera
  // vieja comprueba la generación y aborta en vez de correr una segunda cadena
  // concurrente sobre el mismo entry (consumiría la escalera al doble de
  // velocidad y duplicaría POSTs de subscribe).
  const clientGeneration = useRef(0);

  // subscription(channel) del cliente REUSA el mismo objeto por canal, y
  // delete() de una suscripción nunca creada no la quita del mapa interno:
  // re-suscribirse tras ese ciclo devolvería el mismo objeto y adjuntaría un
  // segundo fan-out idéntico (cada evento entregado 2×). El WeakSet hace la
  // adjunción idempotente por objeto.
  const fanoutAttached = useRef(new WeakSet<object>());

  const requestHeaders = useRef<Record<string, string>>({});
  const workspaceSlug =
    typeof window !== "undefined"
      ? (window.location.pathname.match(/^\/w\/([^/?#]+)/)?.[1] ?? null)
      : null;
  requestHeaders.current = workspaceSlug ? { "x-org": workspaceSlug } : {};

  const applyRequestHeaders = useCallback((request: Request) => {
    for (const [key, value] of Object.entries(requestHeaders.current)) {
      request.headers.set(key, value);
    }
  }, []);

  // Arma (una sola vez) el plazo de resurrección. Si al vencer seguimos sin
  // 'connected', el EventSource nativo ya no va a volver solo (su retry murió
  // con un HTTP no-200) y hay que reconstruir el cliente. Si el retry nativo
  // sí estaba vivo, habría reconectado en ~3s y 'connected' desarma el timer.
  const armReviveWatchdog = useCallback(() => {
    if (providerClosed.current || reviveTimer.current !== null) {
      return;
    }
    reviveTimer.current = setTimeout(() => {
      reviveTimer.current = null;
      reviveClientRef.current();
    }, SSE_REVIVE_AFTER_MS);
  }, []);

  const getTransmit = useCallback(() => {
    if (!transmit.current) {
      // Closure sobre ESTA instancia: los hooks del cliente viejo siguen vivos
      // tras una recreación (un POST en vuelo puede resolver 400 mucho después)
      // y no deben derribar al cliente nuevo y sano.
      let created: Transmit | null = null;
      created = new Transmit({
        baseUrl: baseUrl || (typeof window !== "undefined" ? window.location.origin : ""),
        // El tope por defecto (5 intentos) cierra el EventSource para SIEMPRE si
        // el servidor tarda más de ~15s en volver (un deploy normal): la pestaña
        // queda sin realtime hasta un F5. Reintentar indefinidamente es seguro:
        // el navegador ya espacia los reintentos del EventSource y el contador se
        // resetea en cada reconexión exitosa (que además re-suscribe los canales).
        maxReconnectAttempts: Number.MAX_SAFE_INTEGER,
        // Envolvemos el EventSource para poder observar CADA byte que llega por
        // el stream —incluidos los pings que el servidor manda cada 30s y que
        // el cliente no expone—. Es la única forma de detectar un stream zombi:
        // uno cuyo TCP murió sin FIN, por el que no llega nada, y que por tanto
        // nunca emite `error` ni arma el watchdog existente.
        eventSourceFactory: (url: string | URL, init: { withCredentials: boolean }) => {
          const source = new EventSource(url, init);
          const markActive = () => {
            lastStreamActivityAt.current = Date.now();
          };
          markActive();
          source.addEventListener("message", markActive);
          source.addEventListener("open", markActive);
          return source;
        },
        beforeSubscribe: applyRequestHeaders,
        beforeUnsubscribe: applyRequestHeaders,
        onSubscribeFailed: (response) => {
          console.error("[Transmit] subscription failed", response.status, response.url);
          diagnostic("sse-subscribe-request-failed", {
            status: response.status,
          });
          // 400 del subscribe = la instancia que atendió el POST no conoce el
          // uid de NUESTRO EventSource (visto en prod durante el overlap de un
          // zero-downtime deploy: el SSE queda anclado al contenedor viejo y el
          // POST cae en el nuevo; transmit guarda los clients in-memory por
          // proceso). Reintentar ese POST jamás va a funcionar — la salida es
          // recrear el cliente entero para obtener un uid en la instancia viva.
          if (response.status === 400 && transmit.current === created) {
            recreateClientRef.current();
          }
        },
        onReconnectFailed: () => {
          console.error("[Transmit] reconnect failed");
          diagnostic("sse-reconnect-failed");
        },
      });
      transmit.current = created;

      transmit.current.on("connected", () => {
        if (enableLoggingRef.current) {
          console.log("[SSE] connected");
        }

        sseConnected.current = true;
        if (reviveTimer.current !== null) {
          clearTimeout(reviveTimer.current);
          reviveTimer.current = null;
        }

        diagnostic("sse-connected", {
          afterDisconnect: wasDisconnected.current,
        });

        if (!wasDisconnected.current) {
          return;
        }

        wasDisconnected.current = false;

        // El `open` del cliente solo re-crea suscripciones ya creadas
        // (isCreated): una cuyo POST inicial agotó la escalera de reintentos
        // (~8s, p. ej. la página cargó en pleno reinicio del servidor) quedaba
        // muerta para siempre aunque el SSE volviera. Al reconectar, reintentar
        // también las nunca creadas, con la escalera desde cero.
        // Bump: cualquier escalera pendiente de antes de esta reconexión queda
        // obsoleta (create() se relanza desde cero aquí mismo).
        clientGeneration.current++;
        for (const [entryChannel, entry] of subscriptions.current.entries()) {
          if (!entry.subscription.isCreated && entry.count > 0) {
            diagnostic("sse-subscribe-retry-never-created", {
              channel: entryChannel,
            });
            entry.createAttempt = 0;
            void entry.create();
          }
        }

        for (const callback of reconnectCallbacks.current) {
          try {
            callback();
          } catch (error) {
            console.error("[Transmit] reconnect callback failed", error);
          }
        }
      });
      transmit.current.on("disconnected", () => {
        wasDisconnected.current = true;
        sseConnected.current = false;
        armReviveWatchdog();
        if (enableLoggingRef.current) {
          console.log("[SSE] disconnected");
        }
        diagnostic("sse-disconnected");
      });
      // Tras el primer error el cliente queda en 'reconnecting' y los errores
      // siguientes ya NO emiten 'disconnected': este listener mantiene armado
      // el watchdog aunque la caída empiece directamente en ese estado.
      transmit.current.on("reconnecting", () => {
        sseConnected.current = false;
        armReviveWatchdog();
        if (enableLoggingRef.current) {
          console.log("[SSE] reconnecting...");
        }
      });

      if (enableLoggingRef.current) {
        transmit.current.on("initializing", () => console.log("[SSE] initializing..."));
      }
    }

    return transmit.current;
  }, [applyRequestHeaders, armReviveWatchdog, baseUrl, diagnostic]);

  // Fan-out incoming messages on a channel to all registered callbacks. Lives
  // outside subscribe() because a recreated client needs fresh Subscription
  // objects with this same wiring.
  const attachFanout = useCallback(
    (channel: string, subscription: ReturnType<Transmit["subscription"]>) => {
      if (fanoutAttached.current.has(subscription)) {
        return;
      }
      fanoutAttached.current.add(subscription);

      subscription.onMessage<TransmitEvent>((event) => {
        if (enableLoggingRef.current) {
          console.log(`[Transmit] ${channel} message:`, event);
        }
        onMessageRef.current?.(channel, event);

        const entry = subscriptions.current.get(channel);
        if (entry) {
          for (const cb of entry.callbacks) {
            cb(event);
          }
        }
      });
    },
    [],
  );

  // Núcleo compartido de descarte + reconstrucción: cierra el cliente viejo
  // (EventSource + uid) y levanta uno nuevo, migrando las suscripciones
  // activas. Los dos disparadores (400 de subscribe y watchdog de EventSource
  // cadáver) aportan sus propios guards/presupuesto ANTES de llamar aquí.
  const rebuildClient = useCallback(() => {
    // Invalida las escaleras de reintento pendientes del cliente viejo.
    clientGeneration.current++;

    const previous = transmit.current;
    transmit.current = null;
    try {
      previous?.close();
    } catch {
      // @expected-error-boundary: el cliente viejo ya está roto — si su close()
      // lanza, no hay nada que hacer con él; el nuevo cliente sigue adelante.
    }

    // Marcar la reconexión ANTES de crear el cliente nuevo: cuando su
    // EventSource conecte, el handler 'connected' re-crea las suscripciones
    // (todas quedan isCreated=false sobre el cliente nuevo) y dispara los
    // resync de onReconnect para recuperar lo perdido mientras estuvimos sordos.
    wasDisconnected.current = true;

    const next = getTransmit();
    for (const [channel, entry] of subscriptions.current.entries()) {
      entry.subscription = next.subscription(channel);
      entry.createAttempt = 0;
      attachFanout(channel, entry.subscription);
    }
  }, [getTransmit, attachFanout]);

  // Disparador 1 — el servidor responde 400 al subscribe: ese uid no existe en
  // la instancia que atiende y no va a existir; recrear con cooldown y
  // presupuesto (el 400 también puede ser canal no autorizado, ver arriba).
  const recreateClient = useCallback(() => {
    // El provider ya se desmontó (iframe destruido / swap de página): crear un
    // cliente aquí abriría un EventSource huérfano que nadie cerraría jamás.
    if (providerClosed.current) {
      return;
    }

    const now = Date.now();
    const sinceLast = now - lastClientRecreateAt.current;
    if (sinceLast < CLIENT_RECREATE_COOLDOWN_MS) {
      // No descartar la petición: la escalera de subscribe (~8s) se agota
      // dentro del cooldown, así que sin este diferido un overlap de deploy
      // de más de 10s dejaría el canal muerto sin nada que lo reviva.
      if (pendingRecreate.current === null) {
        pendingRecreate.current = setTimeout(() => {
          pendingRecreate.current = null;
          recreateClientRef.current();
        }, CLIENT_RECREATE_COOLDOWN_MS - sinceLast);
      }
      return;
    }
    if (clientRecreatesLeft.current <= 0) {
      diagnostic("sse-client-recreate-exhausted");
      return;
    }
    lastClientRecreateAt.current = now;
    clientRecreatesLeft.current--;

    diagnostic("sse-client-recreate", {
      attemptsLeft: clientRecreatesLeft.current,
    });

    rebuildClient();
  }, [rebuildClient, diagnostic]);

  // Disparador 2 — watchdog: el EventSource lleva SSE_REVIVE_AFTER_MS caído y
  // el retry nativo no lo revivió (murió con un HTTP no-200 del proxy, típico
  // en deploys). Reconstruir es la única salida. SIN presupuesto: esto no es
  // el churn de un canal no autorizado (400) sino un transporte muerto, y el
  // ritmo ya está acotado por el propio plazo del watchdog (~1 intento/15s;
  // si el nuevo EventSource también muere, sus eventos re-arman el watchdog).
  const reviveClient = useCallback(() => {
    if (providerClosed.current || transmit.current === null) {
      return;
    }
    if (sseConnected.current !== false) {
      return;
    }

    diagnostic("sse-watchdog-revive");
    rebuildClient();
  }, [rebuildClient, diagnostic]);

  useEffect(() => {
    // En efecto (no en render): una escritura de ref durante un render que
    // React descarta dejaría el trigger apuntando a un closure no commiteado.
    recreateClientRef.current = recreateClient;
  }, [recreateClient]);

  useEffect(() => {
    reviveClientRef.current = reviveClient;
  }, [reviveClient]);

  // Disparador 3 — stream zombi: el cliente se cree CONECTADO pero no llega
  // nada por el stream. El watchdog de arriba no cubre este caso porque exige
  // haber visto `disconnected`/`reconnecting`, y un TCP que muere sin FIN
  // (cambio de red, suspender/despertar) nunca emite `error`. Como el servidor
  // pinguea cada 30s, el silencio prolongado es señal fiable de muerte.
  useEffect(() => {
    const interval = setInterval(() => {
      if (providerClosed.current || transmit.current === null) {
        return;
      }

      const silent = shouldReviveForSilence({
        connected: sseConnected.current === true,
        lastActivityAt: lastStreamActivityAt.current,
        now: Date.now(),
        timeoutMs: SSE_SILENCE_TIMEOUT_MS,
      });

      if (!silent) {
        return;
      }

      diagnostic("sse-silence-revive", {
        silentMs: Date.now() - (lastStreamActivityAt.current ?? 0),
      });
      // Marcar antes de reconstruir: si no, el siguiente tick vuelve a ver el
      // mismo silencio y encadena reconstrucciones mientras el nuevo stream
      // todavía no ha recibido su primer byte.
      lastStreamActivityAt.current = Date.now();
      rebuildClient();
    }, SSE_SILENCE_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [rebuildClient, diagnostic]);

  // Al volver la pestaña a visible, revivir de inmediato si el SSE está caído:
  // los timers de pestañas en segundo plano van estrangulados (el watchdog
  // puede tardar minutos en disparar) y este es justo el momento en que el
  // usuario vuelve a mirar la pantalla y espera realtime vivo.
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (reviveTimer.current !== null) {
        clearTimeout(reviveTimer.current);
        reviveTimer.current = null;
      }
      reviveClientRef.current();
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    const subscriptionEntries = subscriptions.current;

    return () => {
      if (enableLoggingRef.current) {
        console.log("[Transmit] - Closing connection");
      }

      providerClosed.current = true;
      if (pendingRecreate.current !== null) {
        clearTimeout(pendingRecreate.current);
        pendingRecreate.current = null;
      }
      if (reviveTimer.current !== null) {
        clearTimeout(reviveTimer.current);
        reviveTimer.current = null;
      }
      transmit.current?.close();
      transmit.current = null;
      subscriptionEntries.clear();
    };
  }, []);

  // Subscribe to a channel with reference counting for cleanup.
  // Uses a debounced unsubscribe so that unmount + immediate remount
  // (Inertia navigation) does not tear down the underlying SSE subscription.
  const subscribe = useCallback(
    (
      channel: string,
      callback: (event: TransmitEvent) => void,
      options: TransmitSubscribeOptions = {},
    ) => {
      if (options.headers) {
        requestHeaders.current = {
          ...requestHeaders.current,
          ...options.headers,
        };
      }

      if (enableLoggingRef.current) {
        console.log("[Transmit] - Subscribing to", channel);
      }

      let sub = subscriptions.current.get(channel);
      let shouldCreateSubscription = false;
      let createSubscription = async () => undefined;

      if (!sub) {
        sub = {
          count: 0,
          subscription: getTransmit().subscription(channel),
          callbacks: new Set(),
          pendingDelete: null,
          createAttempt: 0,
          create: async () => undefined,
        };
        subscriptions.current.set(channel, sub);

        attachFanout(channel, sub.subscription);

        shouldCreateSubscription = true;
        createSubscription = async () => {
          const entry = subscriptions.current.get(channel);
          if (!entry || entry.count === 0) {
            return;
          }

          try {
            await entry.subscription.create();
          } catch (error) {
            console.error("Failed to create subscription:", error);
          }

          if (entry.subscription.isCreated) {
            diagnostic("sse-subscribe-created", {
              channel,
              attempt: entry.createAttempt,
            });
            entry.createAttempt = 0;
            // Reponer el presupuesto de recreación SOLO cuando todos los
            // canales activos están suscritos: si se repusiera con cualquier
            // éxito, un único canal que 400ea para siempre (p. ej. sin
            // autorización) junto a uno sano mantendría el bucle de recreación
            // indefinidamente — el caso exacto que el tope debe cortar.
            const allCreated = [...subscriptions.current.values()].every(
              (other) => other.count === 0 || other.subscription.isCreated,
            );
            if (allCreated) {
              clientRecreatesLeft.current = CLIENT_RECREATE_MAX_ATTEMPTS;
            }
            return;
          }

          const retryDelay = SUBSCRIBE_RETRY_DELAYS_MS[entry.createAttempt];
          if (retryDelay === undefined) {
            console.error("[Transmit] subscription failed after retries", channel);
            diagnostic("sse-subscribe-dead", { channel });
            return;
          }

          diagnostic("sse-subscribe-retrying", {
            channel,
            attempt: entry.createAttempt,
            retryInMs: retryDelay,
          });

          entry.createAttempt++;
          const generation = clientGeneration.current;
          window.setTimeout(() => {
            // Escalera obsoleta: el cliente se recreó (o reconectó) después de
            // programar este reintento y ya relanzó create() desde cero.
            if (generation !== clientGeneration.current) {
              return;
            }
            void createSubscription();
          }, retryDelay);
        };
        sub.create = createSubscription;
      }

      // If a pending delete was scheduled (from a previous unmount), cancel it
      if (sub.pendingDelete !== null) {
        clearTimeout(sub.pendingDelete);
        sub.pendingDelete = null;
      }

      sub.callbacks.add(callback);
      sub.count++;

      if (shouldCreateSubscription) {
        void createSubscription();
      } else if (sub.count === 1 && !sub.subscription.isCreated) {
        // Entry reutilizado que quedó sin crear (p. ej. migrado por una
        // recreación del cliente mientras count era 0, o cuya escalera murió):
        // nadie más va a crearlo — el loop de 'connected' saltó los count===0.
        sub.createAttempt = 0;
        void sub.create();
      }

      if (enableLoggingRef.current) {
        console.log("[Transmit] - Subscribed to", channel);
      }

      // Return unsubscribe function
      return () => {
        const entry = subscriptions.current.get(channel);
        if (!entry) {
          return;
        }

        entry.callbacks.delete(callback);
        entry.count--;

        if (entry.count === 0) {
          // Debounce the actual unsubscribe to survive unmount/remount cycles
          // (e.g. Inertia navigation without persistent layouts)
          entry.pendingDelete = setTimeout(() => {
            // Re-check: a new subscriber may have arrived during the timeout
            if (entry.count === 0) {
              entry.subscription.delete();
              subscriptions.current.delete(channel);
            }
            entry.pendingDelete = null;
          }, 200);
        }
      };
    },
    [getTransmit, attachFanout, diagnostic],
  );

  const onReconnect = useCallback((callback: () => void) => {
    reconnectCallbacks.current.add(callback);

    return () => {
      reconnectCallbacks.current.delete(callback);
    };
  }, []);

  return (
    <TransmitContext.Provider value={{ subscribe, onReconnect }}>
      {children}
    </TransmitContext.Provider>
  );
}

/**
 * Hook to access the Transmit context
 * Must be used within a TransmitProvider
 */
export function useTransmit() {
  const context = useContext(TransmitContext);
  if (!context) {
    throw new Error("useTransmit must be used within a TransmitProvider");
  }
  return context;
}
